import {
  alpha,
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Container,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack, Edit, PersonOutline } from "@mui/icons-material";
import FormActions from "components/FormActions";
import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getClassrooms,
  updateNursery as updateNurseryApi,
  getRegions,
  getCountries,
} from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { stringAvatar } from "utils";
import staticCountries from "components/data";

type Classroom = { id: number; name: string; nurseryId: number };

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const req = (label: string) => (
  <>
    {label} <span style={{ color: "#d32f2f" }}>*</span>
  </>
);

const EditStaff: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const item = location.state as any;
  const { register, handleSubmit, setValue } = useForm<any>({
    defaultValues: item,
  });
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState<File>();
  const [classroomIds, setClassroomIds] = useState<number[]>(
    Array.isArray(item?.classrooms)
      ? item.classrooms.map((c: { id: number }) => c.id)
      : [],
  );
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [role, setRole] = useState(item?.role || "");
  const [country, setCountry] = useState(item?.address?.country || "Algeria");
  const [countries, setCountries] = useState<
    { code: string; name: string; displayName: string }[]
  >([]);
  const [regions, setRegions] = useState<
    { code: string; name: string; displayName?: string }[]
  >([]);
  const [regionLabel, setRegionLabel] = useState("City");

  useEffect(() => {
    getClassrooms().then((data) => setClassrooms(data || []));
    getCountries().then((data) =>
      setCountries(
        data.length
          ? data
          : staticCountries.map((c: any) => ({ ...c, displayName: c.name })),
      ),
    );
  }, [i18n.language]);

  useEffect(() => {
    const countryEntry =
      countries.find((c) => c.name === country) ||
      staticCountries.find((c) => c.name === country);
    if (countryEntry) {
      getRegions(countryEntry.code).then((data) => {
        setRegions(data.regions || []);
        setRegionLabel(data.label || "City");
      });
    } else {
      setRegions([]);
      setRegionLabel("City");
    }
  }, [country, countries, i18n.language]);

  useEffect(() => {
    if (photo) setValue("photo", photo);
  }, [photo, setValue]);

  if (!item) {
    navigate("/nursery");
    return null;
  }

  const onSubmit: SubmitHandler<any> = (data) => {
    setSubmitting(true);
    updateNurseryApi({
      info: { ...data, id: item.id, role },
      userId: item.id,
      dateOfBirth: item.dateOfBirth || "",
      classroomIds,
      setPass: false,
    })
      .then(() => {
        enqueueSnackbar(t("nursery.updated"), { variant: "success" });
        navigate("/nursery");
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.message || "Failed", {
          variant: "error",
        }),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => navigate("/nursery")}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <PersonOutline sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("nursery.edit")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 3, maxWidth: 600 }} maxWidth={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" justifyContent="center" mb={3}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <Fab
                  size="small"
                  component="label"
                  color="primary"
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                >
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.length) setPhoto(e.target.files[0]);
                    }}
                  />
                  <Edit sx={{ fontSize: 16 }} />
                </Fab>
              }
            >
              <Avatar
                src={
                  photo
                    ? URL.createObjectURL(photo)
                    : (item.photo as string | undefined)
                }
                {...(!photo && !item.photo && item.firstName
                  ? stringAvatar(`${item.firstName} ${item.lastName}`, {
                      width: 88,
                      height: 88,
                      fontSize: 32,
                      border: `3px solid ${theme.palette.divider}`,
                    })
                  : {
                      sx: {
                        width: 88,
                        height: 88,
                        fontSize: 32,
                        border: `3px solid ${theme.palette.divider}`,
                      },
                    })}
              />
            </Badge>
          </Box>

          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("labels.general_info")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.first_name"))}
                    fullWidth
                    size="small"
                    {...register("firstName")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.last_name"))}
                    fullWidth
                    size="small"
                    {...register("lastName")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.email"))}
                    fullWidth
                    size="small"
                    {...register("email")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.mobile"))}
                    fullWidth
                    size="small"
                    {...register("mobile")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.role"))}</InputLabel>
                    <Select
                      label={req(t("inputs.role"))}
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="ROOM_LEADER">
                        {t("labels.room_leader")}
                      </MenuItem>
                      <MenuItem value="NURSERY_PRACTITIONER">
                        {t("labels.nursery_practitioner")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t("inputs.classroom")}</InputLabel>
                    <Select
                      multiple
                      label={t("inputs.classroom")}
                      value={classroomIds}
                      onChange={(e) => {
                        const v = e.target.value;
                        setClassroomIds(
                          typeof v === "string"
                            ? v.split(",").map(Number)
                            : (v as number[]),
                        );
                      }}
                      renderValue={(selected) =>
                        (selected as number[])
                          .map(
                            (id) =>
                              classrooms.find((c) => c.id === id)?.name ?? id,
                          )
                          .join(", ")
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      {classrooms.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("address")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={req(t("inputs.address_line1"))}
                    fullWidth
                    size="small"
                    {...register("address.line1")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("inputs.address_line2")}
                    fullWidth
                    size="small"
                    {...register("address.line2")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={4}>
                  {regions.length > 0 ? (
                    <FormControl fullWidth size="small">
                      <InputLabel>{req(regionLabel)}</InputLabel>
                      <Select
                        label={req(regionLabel)}
                        defaultValue={item.address?.city || ""}
                        {...register("address.city")}
                        sx={{ borderRadius: 2 }}
                      >
                        {regions.map((r) => (
                          <MenuItem key={r.code} value={r.name}>
                            {r.code} - {r.displayName || r.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      label={req(t("inputs.city"))}
                      fullWidth
                      size="small"
                      {...register("address.city")}
                      sx={inputSx}
                    />
                  )}
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={req(t("inputs.post_code"))}
                    fullWidth
                    size="small"
                    {...register("address.postcode")}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.country"))}</InputLabel>
                    <Select
                      label={req(t("inputs.country"))}
                      value={country}
                      {...register("address.country", {
                        onChange: (e) => setCountry(e.target.value),
                      })}
                      sx={{ borderRadius: 2 }}
                    >
                      {countries.map((c) => (
                        <MenuItem key={c.code} value={c.name}>
                          {c.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <FormActions
            onCancel={() => navigate("/nursery")}
            submitting={submitting}
          />
        </form>
      </Container>
    </>
  );
};

export default EditStaff;
